import React from 'react';
import { makeStyles, useMediaQuery, useTheme } from '@material-ui/core';
import Typography from '../../overrides/Typography';
import PropTypes from 'prop-types';
import { ReactComponent as BenefitsIcon } from '../../../assets/icons/grv-benefits-small.svg';
import { ReactComponent as ClockIcon } from '../../../assets/icons/clock.svg';
import { ReactComponent as UserIcon } from '../../../assets/icons/user.svg';
import { ReactComponent as CalendarIcon } from '../../../assets/icons/calendar.svg';
import { EXCLUSIVE_TABLE_TEXT, GUEST_INFLECTION } from '../../../assets/copy';
import createInflection from '../../../utils/inflection/createInflection';
import clsx from 'clsx';

const NARROW_SCREEN_BREAKPOINT = 420;

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
  },
  flexbox: {
    display: 'flex',
    alignItems: `center`,
  },
  image: {
    maxWidth: '120px',
    minWidth: '100px',
    minHeight: '120px',
    borderRadius: '4px 0 0 4px',
    objectFit: 'cover',
    alignSelf: 'stretch',
  },
  reservationDetailsContainer: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '120px',
    border: '1px solid #E8E5E1',
    borderLeft: 'none',
    borderRadius: '0 4px 4px 0',
    boxSizing: 'border-box',
    justifyContent: 'center',
    padding: theme.spacing(1.5, 2),
    flexGrow: 1,
  },
  icon: {
    width: '16px',
    height: '16px',
    margin: 'auto',
  },
  insider: {
    width: '16px',
    height: '16px',
    marginBottom: '2px',
    '& path': {
      fill: `${theme.palette.yellow[40]} !important`,
    },
    [theme.breakpoints.up('sm')]: {
      marginLeft: theme.spacing(1),
      marginRight: theme.spacing(0.25),
    },
    [theme.breakpoints.down(NARROW_SCREEN_BREAKPOINT)]: {
      marginTop: theme.spacing(0.5),
    },
  },
  cardholderLabel: {
    color: theme.palette.secondary['65'],
    [theme.breakpoints.up('sm')]: {
      marginLeft: theme.spacing(0.25),
    },
    [theme.breakpoints.down(NARROW_SCREEN_BREAKPOINT)]: {
      marginTop: theme.spacing(0.5),
    },
  },
  twoColGrid: {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    rowGap: 0,
    columnGap: theme.spacing(1),
  },
  noWrap: {
    '& p': {
      whiteSpace: 'nowrap',
    },
  },
  narrowCardholderTable: {
    padding: theme.spacing(0, 0.5),
  },
}));

const ReservationCard = ({
  imageUrl,
  venueName,
  reservationDate,
  reservationTime,
  guestCount,
  isCardholderTable,
  classes: customClasses,
}) => {
  const classes = useStyles({ classes: customClasses });
  const guestAltText = createInflection(GUEST_INFLECTION, guestCount);
  const theme = useTheme();
  const isNarrowScreen = useMediaQuery(
    theme.breakpoints.down(NARROW_SCREEN_BREAKPOINT)
  );

  return (
    <div className={clsx(classes.root, classes.flexbox)}>
      <img src={imageUrl} className={classes.image} alt="" />
      <div className={classes.reservationDetailsContainer}>
        <Typography variant="medium1Semibold">{venueName}</Typography>
        <div className={classes.twoColGrid}>
          <CalendarIcon className={classes.icon} role="presentation" />
          <Typography variant="small4Normal">{reservationDate}</Typography>

          <ClockIcon className={classes.icon} role="presentation" />
          <div className={clsx(classes.flexbox, classes.noWrap)}>
            <Typography variant="small4Normal">{reservationTime}</Typography>
            {isCardholderTable && !isNarrowScreen && (
              <>
                <BenefitsIcon className={classes.insider} role="presentation" />
                <Typography
                  variant="tiny1Normal"
                  className={classes.cardholderLabel}>
                  {EXCLUSIVE_TABLE_TEXT}
                </Typography>
              </>
            )}
          </div>

          {isCardholderTable && isNarrowScreen && (
            <>
              <BenefitsIcon className={classes.insider} role="presentation" />
              <Typography
                variant="tiny1Normal"
                className={classes.cardholderLabel}>
                {EXCLUSIVE_TABLE_TEXT}
              </Typography>
            </>
          )}

          <UserIcon className={classes.icon} role="presentation" />
          <Typography variant="small4Normal">{guestAltText}</Typography>
        </div>
      </div>
    </div>
  );
};

ReservationCard.propTypes = {
  imageUrl: PropTypes.string.isRequired,
  venueName: PropTypes.string.isRequired,
  reservationDate: PropTypes.string.isRequired,
  reservationTime: PropTypes.string.isRequired,
  guestCount: PropTypes.number.isRequired,
  isCardholderTable: PropTypes.bool,
};

export default ReservationCard;
